const VARINT_PAYLOAD_MASK = 0x7f;
/** Tag of the repeated layers field (3) of a vector tile, length-delimited */
const TILE_LAYER_TAG = 0x1a;
const COMMAND_MOVE_TO = 1;
const COMMAND_CLOSE_PATH = 7;

/** Geometry types of vector tile features */
export enum VectorTileGeometryType {
  point = 1,
  line = 2,
  polygon = 3,
}

/** A point in vector tile coordinates (0..extent, Y pointing south) */
export type TilePoint = { x: number; y: number };

/** A decoded vector tile feature */
export type VectorTileFeature = {
  type: number;
  properties: Record<string, string | number | boolean>;
  /** The command-encoded geometry */
  geometry: number[];
};

/** A decoded vector tile layer */
export type VectorTileLayer = { extent: number; features: VectorTileFeature[] };

/** Minimal protobuf reader for the subset of the format used by vector tiles */
class ProtobufReader {
  position = 0;

  constructor(
    private readonly bytes: Uint8Array,
    private readonly end = bytes.length,
  ) {}

  hasMore(): boolean {
    return this.position < this.end;
  }

  varint(): number {
    let result = 0;
    let shift = 1;
    let byte: number;
    do {
      byte = this.bytes[this.position++];
      result += (byte & VARINT_PAYLOAD_MASK) * shift;
      shift *= 128;
    } while (byte >= 0x80);
    return result;
  }

  /** @returns A reader for the next length-delimited field */
  message(): ProtobufReader {
    const length = this.varint();
    const reader = new ProtobufReader(this.bytes, this.position + length);
    reader.position = this.position;
    this.position += length;
    return reader;
  }

  string(): string {
    const reader = this.message();
    return new TextDecoder().decode(
      this.bytes.subarray(reader.position, reader.end),
    );
  }

  packedVarints(): number[] {
    const reader = this.message();
    const values: number[] = [];
    while (reader.hasMore()) values.push(reader.varint());
    return values;
  }

  float(): number {
    const value = new DataView(
      this.bytes.buffer,
      this.bytes.byteOffset + this.position,
      4,
    ).getFloat32(0, true);
    this.position += 4;
    return value;
  }

  double(): number {
    const value = new DataView(
      this.bytes.buffer,
      this.bytes.byteOffset + this.position,
      8,
    ).getFloat64(0, true);
    this.position += 8;
    return value;
  }

  skip(wireType: number): void {
    switch (wireType) {
      case 0: {
        this.varint();
        break;
      }
      case 1: {
        this.position += 8;
        break;
      }
      case 2: {
        this.message();
        break;
      }
      case 5: {
        this.position += 4;
        break;
      }
      default:
        throw new Error(`Unsupported protobuf wire type ${wireType}`);
    }
  }
}

function zigzag(value: number): number {
  return value % 2 === 0 ? value / 2 : -(value + 1) / 2;
}

function readValue(reader: ProtobufReader): string | number | boolean {
  let value: string | number | boolean = "";
  while (reader.hasMore()) {
    const tag = reader.varint();
    const field = Math.floor(tag / 8);
    switch (field) {
      case 1: {
        value = reader.string();
        break;
      }
      case 2: {
        value = reader.float();
        break;
      }
      case 3: {
        value = reader.double();
        break;
      }
      case 4:
      case 5: {
        value = reader.varint();
        break;
      }
      case 6: {
        value = zigzag(reader.varint());
        break;
      }
      case 7: {
        value = reader.varint() !== 0;
        break;
      }
      default:
        reader.skip(tag % 8);
    }
  }
  return value;
}

function readFeature(feature: ProtobufReader): {
  type: number;
  tags: number[];
  geometry: number[];
} {
  const raw = {
    type: 0,
    tags: new Array<number>(),
    geometry: new Array<number>(),
  };
  while (feature.hasMore()) {
    const tag = feature.varint();
    switch (Math.floor(tag / 8)) {
      case 2: {
        raw.tags = feature.packedVarints();
        break;
      }
      case 3: {
        raw.type = feature.varint();
        break;
      }
      case 4: {
        raw.geometry = feature.packedVarints();
        break;
      }
      default:
        feature.skip(tag % 8);
    }
  }
  return raw;
}

function readLayer(layer: ProtobufReader): [string, VectorTileLayer] {
  let name = "";
  let extent = 4096;
  const keys: string[] = [];
  const values: Array<string | number | boolean> = [];
  const rawFeatures: Array<ReturnType<typeof readFeature>> = [];
  while (layer.hasMore()) {
    const tag = layer.varint();
    switch (Math.floor(tag / 8)) {
      case 1: {
        name = layer.string();
        break;
      }
      case 2: {
        rawFeatures.push(readFeature(layer.message()));
        break;
      }
      case 3: {
        keys.push(layer.string());
        break;
      }
      case 4: {
        values.push(readValue(layer.message()));
        break;
      }
      case 5: {
        extent = layer.varint();
        break;
      }
      default:
        layer.skip(tag % 8);
    }
  }
  const features = rawFeatures.map(({ type, tags, geometry }) => {
    const properties: VectorTileFeature["properties"] = {};
    for (let i = 0; i + 1 < tags.length; i += 2) {
      properties[keys[tags[i]]] = values[tags[i + 1]];
    }
    return { type, properties, geometry };
  });
  return [name, { extent, features }];
}

/**
 * @param bytes A Mapbox vector tile
 * @returns The layers of the tile by name
 */
export function readVectorTile(
  bytes: Uint8Array,
): Map<string, VectorTileLayer> {
  const layers = new Map<string, VectorTileLayer>();
  const tile = new ProtobufReader(bytes);
  while (tile.hasMore()) {
    const tag = tile.varint();
    if (tag === TILE_LAYER_TAG) layers.set(...readLayer(tile.message()));
    else tile.skip(tag % 8);
  }
  return layers;
}

/**
 * @param geometry The command-encoded geometry of a feature
 * @returns The parts of the geometry (lines, or polygon rings) in drawing order
 */
export function decodeGeometry(geometry: number[]): TilePoint[][] {
  const parts: TilePoint[][] = [];
  let x = 0;
  let y = 0;
  let i = 0;
  while (i < geometry.length) {
    const command = geometry[i] & 0x7;
    const count = geometry[i] >> 3;
    i++;
    if (command === COMMAND_CLOSE_PATH) continue;
    for (let n = 0; n < count; n++) {
      x += zigzag(geometry[i++]);
      y += zigzag(geometry[i++]);
      if (command === COMMAND_MOVE_TO) parts.push([]);
      parts.at(-1)?.push({ x, y });
    }
  }
  return parts;
}
